import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  nav,
  surface,
  text,
} from "@atlas-int/tokens";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import VizTypeSelectorButton from "../../../components/buttons/VizTypeSelectorButton";
import { TradeDirection } from "@atlas-int/data/types";
import { VizType, UIView } from "@atlas-int/data";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const DemoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[8]};
  align-items: flex-start;
`;

const DemoGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DemoLabel = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  margin-bottom: ${spacing[2]};
`;

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const Note = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[6]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[6]};
`;

const DarkStrip = styled.div`
  display: flex;
  background-color: ${nav.background};
  border-radius: ${borderRadius.base};
  overflow: hidden;
  height: 52px;
`;

const VizTypeStrip = styled(DarkStrip)`
  height: 60px;
`;

// ─── Visual replicas for hook-dependent buttons ─────────────────────────────
// These replicate the styling from the original components but use local state
// instead of usePageQueryParams, so they render standalone on the demo page.

const UIViewButton = styled.button<{ $selected?: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.lg};
  padding: ${spacing[1]} ${spacing[2]};
  justify-content: space-around;
  flex-grow: 1;
  flex-basis: 0px;
  font-size: ${fontSize.sm};
  height: 100%;
  box-sizing: border-box;
  border: none;
  background: none;
  color: rgb(58, 65, 72);
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};

  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? "none" : interactive.muted)};
  }

  ${({ $selected }) =>
    $selected &&
    `
    background-color: ${interactive.default};
    color: ${text.onDark};
  `}
`;

const UIViewStrip = styled.div`
  display: flex;
  background-color: ${surface.panel};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  overflow: hidden;
  height: 36px;
  width: 220px;
`;

const TradeDirectionItem = styled.li<{ $selected?: boolean }>`
  margin: ${spacing[0]} 3px ${spacing[0]} ${spacing[0]};
  padding: ${spacing[0]};
  font-size: ${fontSize.sm};
  color: ${({ $selected }) => ($selected ? "black" : text.muted)};
  font-weight: ${({ $selected }) => ($selected ? fontWeight.bold : "normal")};
  cursor: pointer;
  list-style: none;
`;

const TradeDirectionRow = styled.ul`
  display: flex;
  gap: ${spacing[2]};
  list-style: none;
  padding: 0;
  margin: 0;
`;

// ─── VizTypeSelectorButton Section ──────────────────────────────────────────

const VizTypeSelectorDemo: React.FC = () => (
  <SubSection>
    <SubTitle>VizTypeSelectorButton</SubTitle>
    <Description>
      Visualization type selector buttons with icons. Each VizType enum value maps
      to a label and SVG icon.
    </Description>
    <DemoRow>
      <DemoGroup>
        <DemoLabel>All visualization types</DemoLabel>
        <VizTypeStrip>
          {[
            VizType.Tree,
            VizType.Geo,
            VizType.OverTime,
            VizType.MarketShare,
            VizType.ProductSpace,
            VizType.Feasibility,
          ].map((type) => (
            <VizTypeSelectorButton key={type} vizType={type} />
          ))}
        </VizTypeStrip>
      </DemoGroup>
    </DemoRow>
    <CodeBlock
      code={`import VizTypeSelectorButton from "components/buttons/VizTypeSelectorButton";

<VizTypeSelectorButton vizType={VizType.Tree} />
<VizTypeSelectorButton vizType={VizType.Geo} />
<VizTypeSelectorButton vizType={VizType.OverTime} />`}
    />
  </SubSection>
);

// ─── UIViewSelectorButton Section ───────────────────────────────────────────

const UIViewSelectorDemo: React.FC = () => {
  const [selected, setSelected] = useState<"products" | "markets">("products");

  return (
    <SubSection>
      <SubTitle>UIViewSelectorButton</SubTitle>
      <Description>
        Toggle button for switching between Locations (Markets) and Products views.
        Products view is disabled on the Geomap visualization.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>Interactive toggle</DemoLabel>
          <UIViewStrip>
            <UIViewButton
              $selected={selected === "markets"}
              onClick={() => setSelected("markets")}
            >
              Locations
            </UIViewButton>
            <UIViewButton
              $selected={selected === "products"}
              onClick={() => setSelected("products")}
            >
              Products
            </UIViewButton>
          </UIViewStrip>
        </DemoGroup>
        <DemoGroup>
          <DemoLabel>Products disabled (Geomap)</DemoLabel>
          <UIViewStrip>
            <UIViewButton $selected>Locations</UIViewButton>
            <UIViewButton $disabled>Products</UIViewButton>
          </UIViewStrip>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import UIViewSelectorButton from "components/buttons/UIViewSelectorButton";

<UIViewSelectorButton uiViewOption={UIView.Markets} selected={true} vizType={VizType.Tree} />
<UIViewSelectorButton uiViewOption={UIView.Products} selected={false} vizType={VizType.Tree} />

// Products view is disabled on Geomap:
<UIViewSelectorButton uiViewOption={UIView.Products} selected={false} vizType={VizType.Geo} />`}
      />
    </SubSection>
  );
};

// ─── TradeDirectionSelectorButton Section ───────────────────────────────────

const TradeDirectionDemo: React.FC = () => {
  const [direction, setDirection] = useState<TradeDirection>(TradeDirection.Exports);

  return (
    <SubSection>
      <SubTitle>TradeDirectionSelectorButton</SubTitle>
      <Description>
        Toggle between Exporter and Importer trade directions. In Markets view,
        switching direction also swaps the exporter/importer locations.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>Interactive toggle</DemoLabel>
          <TradeDirectionRow>
            <TradeDirectionItem
              $selected={direction === TradeDirection.Exports}
              onClick={() => setDirection(TradeDirection.Exports)}
            >
              Exporter
            </TradeDirectionItem>
            <TradeDirectionItem
              $selected={direction === TradeDirection.Imports}
              onClick={() => setDirection(TradeDirection.Imports)}
            >
              Importer
            </TradeDirectionItem>
          </TradeDirectionRow>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import TradeDirectionSelectorButton from "components/buttons/TradeDirectionSelectorButton";
import { TradeDirection } from "@atlas-int/data/types";
import { UIView, VizType } from "@atlas-int/data";

<TradeDirectionSelectorButton targetOption={TradeDirection.Exports} selected={true} />
<TradeDirectionSelectorButton targetOption={TradeDirection.Imports} selected={false} />`}
      />
    </SubSection>
  );
};

// ─── Modal Trigger Buttons Section ──────────────────────────────────────────

const ModalButtonsDemo: React.FC = () => (
  <SubSection>
    <SubTitle>Modal Trigger Buttons</SubTitle>
    <Description>
      DownloadModalButton, GuideModalButton, and ShareModalButton are self-contained
      button+modal combos used in the navigation bar.
    </Description>
    <Note>
      These buttons depend on various contexts: DownloadModalButton uses{" "}
      <code>useDownload</code> for SVG/data refs, GuideModalButton uses localStorage
      and the Walkthrough component, ShareModalButton uses <code>window.location.href</code>.
      All use ModalBase internally.
    </Note>
    <CodeBlock
      code={`import DownloadModalButton from "components/buttons/DownloadModalButton";
import GuideModalButton from "components/buttons/GuideModalButton";
import ShareModalButton from "components/buttons/ShareModalButton";

// Each takes menuOptionClassName and isNavigationBarOpen props:
<DownloadModalButton menuOptionClassName="menu-item" isNavigationBarOpen={true} />
<GuideModalButton menuOptionClassName="menu-item" isNavigationBarOpen={true} />
<ShareModalButton menuOptionClassName="menu-item" isNavigationBarOpen={true} />`}
    />
  </SubSection>
);

// ─── Main Section ────────────────────────────────────────────────────────────

const ButtonVariantsSection: React.FC = () => (
  <PreviewSection
    id="button-variants"
    title="Specialized Buttons"
    description="View selectors, viz type, trade direction, modal triggers"
  >
    <VizTypeSelectorDemo />
    <UIViewSelectorDemo />
    <TradeDirectionDemo />
    <ModalButtonsDemo />
  </PreviewSection>
);

export default ButtonVariantsSection;
